
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';

const Index = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "TrueApply - Your Trusted Job Search Partner",
    "url": window.location.origin,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${window.location.origin}/jobs?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead 
        title="TrueApply - Find Your Dream Job | Trusted Job Portal"
        description="TrueApply is the leading job portal that connects job seekers with top employers. Apply to jobs, track applications, and advance your career with our easy-to-use platform."
        keywords="job search, job portal, employment, career, job application, find jobs, apply online, job opportunities"
        ogTitle="TrueApply - The Smart Way to Find Your Next Job"
        ogDescription="Apply to top jobs with a single click, get interviewed, and land your dream position with TrueApply - the intelligent job portal."
        structuredData={structuredData}
      />
      
      <Header />
      
      <section className="pt-52 pb-16 md:pt-64 md:pb-28">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-apple-blue/10 text-apple-blue font-medium text-sm animate-fade-in">
              Revolutionizing Talent Acquisition
            </div>
            <h1 className="text-4xl md:text-6xl font-semibold text-apple-text mb-6 leading-tight animate-fade-up">
              AI-Powered Hiring for the Modern Organization
            </h1>
            <p className="text-lg md:text-xl text-apple-text-secondary mb-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
              Streamline your recruitment process with smart assessments, AI-powered resume analysis, and video interviews – all in one platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: '200ms' }}>
              <Link 
                to="/signup" 
                className="apple-btn w-full sm:w-auto text-center"
              >
                Apply for a Position
              </Link>
              <Link 
                to="/dashboard" 
                className="apple-btn-secondary w-full sm:w-auto text-center"
              >
                Employer Dashboard
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-text mb-4">
              Reimagine Your Hiring Process
            </h2>
            <p className="text-lg text-apple-text-secondary">
              Our platform brings together the latest in AI technology with human-centered design to make hiring easier, faster, and more effective.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <FeatureCard 
              title="Smart Application Process"
              description="Candidates can easily apply, select job openings, and upload resumes. Our AI analyzes each resume for perfect matching."
              icon="📝"
              delay={0}
            />
            <FeatureCard 
              title="AI-Powered Assessments"
              description="Technical questions tailored to each role, automatically analyzed by our AI to identify top performers."
              icon="🧠"
              delay={100}
            />
            <FeatureCard 
              title="Video Introductions"
              description="Candidates record video introductions, giving you insight into their communication skills and personality."
              icon="🎥"
              delay={200}
            />
            <FeatureCard 
              title="Centralized Dashboard"
              description="All candidate information in one place, with AI-generated summaries for quick decision making."
              icon="📊"
              delay={300}
            />
            <FeatureCard 
              title="Candidate Sharing"
              description="Share candidate profiles with other team members via unique links for collaborative hiring."
              icon="🔗"
              delay={400}
            />
            <FeatureCard 
              title="Easy Export"
              description="Export candidate details and video links for your records or to share with others in your organization."
              icon="📤"
              delay={500}
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-apple-gray">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-text mb-4">
              How It Works
            </h2>
            <p className="text-lg text-apple-text-secondary">
              Our streamlined process makes hiring efficient for both employers and candidates.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="glass-panel rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                    alt="Candidate filling out an application on laptop" 
                    className="w-full aspect-video object-cover"
                  />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-apple-text mb-4">
                  For Candidates
                </h3>
                <ul className="space-y-4">
                  <ProcessStep 
                    number="01" 
                    title="Sign Up" 
                    description="Create your profile with basic information and verify your phone number." 
                  />
                  <ProcessStep 
                    number="02" 
                    title="Choose a Position" 
                    description="Select from available job openings and upload your resume." 
                  />
                  <ProcessStep 
                    number="03" 
                    title="Complete Assessment" 
                    description="Answer role-specific questions to showcase your skills and knowledge." 
                  />
                  <ProcessStep 
                    number="04" 
                    title="Record Introduction" 
                    description="Record a short video introducing yourself and highlighting your experience." 
                  />
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mt-20">
              <div className="md:order-2">
                <div className="glass-panel rounded-2xl overflow-hidden shadow-lg">
                  <div className="aspect-video w-full">
                    <iframe 
                      src="https://player.vimeo.com/video/690177203?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1" 
                      className="w-full h-full"
                      frameBorder="0" 
                      allow="autoplay; fullscreen; picture-in-picture" 
                      allowFullScreen
                      title="Team collaborating on hiring process"
                    ></iframe>
                  </div>
                </div>
              </div>
              <div className="md:order-1">
                <h3 className="text-2xl font-semibold text-apple-text mb-4">
                  For Employers
                </h3>
                <ul className="space-y-4">
                  <ProcessStep 
                    number="01" 
                    title="Review Applications" 
                    description="Access AI-generated resume summaries and assessment results." 
                  />
                  <ProcessStep 
                    number="02" 
                    title="Watch Introductions" 
                    description="View candidate video introductions to assess communication skills." 
                  />
                  <ProcessStep 
                    number="03" 
                    title="Share Candidates" 
                    description="Send unique links to other team members for collaborative hiring." 
                  />
                  <ProcessStep 
                    number="04" 
                    title="Make Decisions" 
                    description="Use our comprehensive dashboard to identify the best candidates." 
                  />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto bg-white rounded-3xl overflow-hidden shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <h2 className="text-3xl font-semibold text-apple-text mb-4">
                  Ready to Transform Your Hiring Process?
                </h2>
                <p className="text-apple-text-secondary mb-8">
                  Join thousands of organizations using TalentSync to find their perfect candidates faster and more effectively.
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <Link 
                    to="/signup" 
                    className="apple-btn w-full sm:w-auto text-center"
                  >
                    Apply Now
                  </Link>
                  <Link 
                    to="/dashboard" 
                    className="apple-btn-secondary w-full sm:w-auto text-center"
                  >
                    Employer Login
                  </Link>
                </div>
              </div>
              <div className="relative overflow-hidden bg-apple-blue/5">
                <div className="absolute inset-0 bg-gradient-to-tr from-apple-blue/10 to-transparent"></div>
                <img 
                  src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" 
                  alt="Team collaborating on hiring process" 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4/5">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                      <div className="text-sm font-medium text-apple-text">AI-Powered Matching</div>
                    </div>
                    <div className="h-2 w-full bg-gray-200 rounded-full mb-2">
                      <div className="h-2 bg-apple-blue rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <div className="text-xs text-apple-text-secondary">
                      Finding the perfect candidate match...
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FeatureCard = ({ title, description, icon, delay = 0 }) => (
  <div 
    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover-scale"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="w-14 h-14 rounded-full bg-apple-blue/10 flex items-center justify-center mb-5 text-2xl">
      {icon}
    </div>
    <h3 className="text-xl font-semibold text-apple-text mb-3">{title}</h3>
    <p className="text-apple-text-secondary">{description}</p>
  </div>
);

const ProcessStep = ({ number, title, description }) => (
  <li className="flex items-start">
    <div className="mr-4 bg-apple-blue text-white text-sm font-medium px-2.5 py-1 rounded">
      {number}
    </div>
    <div>
      <h4 className="font-semibold text-apple-text mb-1">{title}</h4>
      <p className="text-apple-text-secondary">{description}</p>
    </div>
  </li>
);

export default Index;
